<script setup lang="ts">
import { Badge } from '~ds/components/ui/badge';
import { Heading, Text } from '~ds/components/ui/typography';
import { formatCalendarDay } from '~kui/utils/localeFormat';

import type { ArticleData } from '~/graphql/generated/graphql';
import type { Article } from '~/types/Learn/Article';

import { useClusterCountryConfig } from '~/composables/core';
import { articleBylineDays } from '~/utils/article';
import { fromCampaignDateTime } from '~/utils/campaign';

interface Props {
  article: Article;
  data: ArticleData | null;
  badge?: string;
}

const props = withDefaults(defineProps<Props>(), {
  badge: undefined,
});

const { locale } = useI18n();

const { clusterTimezone } = useClusterCountryConfig();

// `displayPublishedAt` only arrives on the admin projection, already as a calendar day, where it
// lets the editor preview an unsaved back-date over the first publish. Public reads get the
// back-date as `publishedAt`. An unpublished draft previews as published today.
const bylineDays = computed(() => articleBylineDays(props.article, clusterTimezone.value));

const displayDate = computed(() => formatCalendarDay({
  date: bylineDays.value.publishedDay ?? fromCampaignDateTime(new Date().toISOString(), clusterTimezone.value) ?? '',
  locale: locale.value,
  withYear: true,
}));

const lastUpdatedDate = computed(() => (bylineDays.value.lastUpdatedDay
  ? formatCalendarDay({ date: bylineDays.value.lastUpdatedDay, locale: locale.value, withYear: true })
  : null));

const primaryConsiderationArea = computed(() => props.article.considerationAreas?.[0]?.label ?? null);
</script>

<template>
  <div class="min-w-0 max-w-3xl lg:col-span-3">
    <Text size="sm" class="mb-4 text-muted-foreground">
      {{ $t('unified.nav.careerAdvice') }}<template v-if="primaryConsiderationArea">
        / {{ primaryConsiderationArea }}
      </template>
      / {{ article.name }}<template v-if="data?.readingTimeMinutes">
        · {{ $t('unified.articles.minRead', { count: data.readingTimeMinutes }) }}
      </template>
    </Text>

    <div class="mb-6 flex items-center gap-2">
      <Badge
        tone="lime"
        class="uppercase"
      >
        {{ badge ?? $t('unified.articles.badge') }}
      </Badge>
      <Badge
        v-if="primaryConsiderationArea"
        variant="outline"
      >
        {{ primaryConsiderationArea }}
      </Badge>
    </div>

    <Heading
      size="3xl"
      as="h1"
      class="mb-4 tracking-tight"
    >
      {{ article.name }}
    </Heading>
    <Text
      v-if="data?.standfirst"
      size="lg"
      class="mb-8 text-muted-foreground"
    >
      {{ data.standfirst }}
    </Text>

    <div class="border-y border-border py-4">
      <div class="flex items-center gap-4">
        <KUIUnifiedBlinky size="md" class="shrink-0" />
        <div>
          <Text
            v-if="data?.authorName"
            size="md"
            class="font-semibold"
          >
            {{ $t('unified.articles.byAuthor', { name: data.authorName }) }}
          </Text>
          <Text size="sm" class="text-muted-foreground">
            <template v-if="data?.readingTimeMinutes">
              {{ $t('unified.articles.minRead', { count: data.readingTimeMinutes }) }} ·
            </template>{{ displayDate }}<template v-if="lastUpdatedDate">
              · {{ $t('unified.articles.lastUpdated', { date: lastUpdatedDate }) }}
            </template>
          </Text>
        </div>
      </div>
    </div>
  </div>
</template>
